import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { downloadBlob } from '../services/api';
import { StatusPill, PriorityPill, fmtDate } from './TasksCommon';
import './Tasks.css';

const CONCLUSIONS = [
  { value: 'pending', label: 'Без ответа', class: 'pending' },
  { value: 'approved', label: 'Согласовано', class: 'approved' },
  { value: 'approved_with_remarks', label: 'С замечаниями', class: 'remarks' },
  { value: 'rejected', label: 'Отклонено', class: 'rejected' },
];

export default function ProjectMatrix({ projectId, onOpenTask, onDispatch }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getProjectMatrix(projectId);
      setData(res.data);
    } catch { /* ignore */ }
    setLoading(false);
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const handleConclusionChange = async (taskId, field, value) => {
    try {
      await api.updateTask(taskId, { [field]: value });
      setData((d) => ({
        ...d,
        rows: d.rows.map((r) => r.task_id === taskId ? { ...r, [field]: value } : r),
        approved_count: d.rows.filter((r) =>
          r.task_id === taskId ? (field === 'conclusion_choice' ? value : r.conclusion_choice) === 'approved'
                               : r.conclusion_choice === 'approved'
        ).length,
      }));
    } catch (e) {
      alert('Не удалось сохранить: ' + (e.response?.data?.detail || e.message));
    }
  };

  if (loading) return <div className="tasks-empty">Загрузка…</div>;
  if (!data) return <div className="tasks-empty">Нет данных</div>;

  const total = data.rows.length;
  const approvedPct = total ? Math.round((data.approved_count * 100) / total) : 0;

  return (
    <div>
      <div className="matrix-summary">
        <div>
          <strong>Согласовано:</strong> {data.approved_count} из {total} задач ({approvedPct}%)
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className="tasks-btn"
            onClick={async () => {
              try {
                const res = await api.downloadProjectMatrixDocx(projectId);
                downloadBlob(res, `Kelishuv-varaqasi-${projectId}.docx`);
              } catch (e) {
                alert('Не удалось скачать: ' + (e.response?.data?.detail || e.message));
              }
            }}
          >
            📄 Word
          </button>
          <button
            className="tasks-btn"
            onClick={async () => {
              try {
                const res = await api.downloadProjectMatrixXlsx(projectId);
                downloadBlob(res, `Kelishuv-varaqasi-${projectId}.xlsx`);
              } catch (e) {
                alert('Не удалось скачать: ' + (e.response?.data?.detail || e.message));
              }
            }}
          >
            📊 Excel
          </button>
          <button className="tasks-btn primary" onClick={onDispatch}>📤 Разослать по отделам</button>
        </div>
      </div>

      {total === 0 ? (
        <div className="tasks-empty">В проекте нет задач. Нажмите «Разослать по отделам» чтобы создать задачи автоматически.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="tasks-table matrix-table">
            <thead>
              <tr>
                <th>Отдел</th>
                <th>Ответственный</th>
                <th>Статус</th>
                <th>Приоритет</th>
                <th>Срок</th>
                <th>Дней</th>
                <th>💬</th>
                <th>📎</th>
                <th>Заключение</th>
                <th>Комментарий</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.task_id} className={r.is_overdue ? 'overdue' : ''}>
                  <td onClick={() => onOpenTask(r.task_id)} style={{ cursor: 'pointer' }}>
                    <strong>{r.department}</strong>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>{r.title}</div>
                  </td>
                  <td onClick={() => onOpenTask(r.task_id)} style={{ cursor: 'pointer' }}>
                    {r.assignee}
                  </td>
                  <td><StatusPill value={r.status} /></td>
                  <td><PriorityPill value={r.priority} /></td>
                  <td>{fmtDate(r.due_date)}</td>
                  <td style={{ color: r.days_left !== null && r.days_left < 0 ? '#dc2626' : '#111', fontWeight: 500 }}>
                    {r.days_left === null ? '—'
                      : r.days_left < 0 ? `просрочено ${-r.days_left}`
                      : r.days_left === 0 ? 'сегодня'
                      : r.days_left}
                  </td>
                  <td>{r.comments_count || ''}</td>
                  <td>{r.attachments_count || ''}</td>
                  <td>
                    <select
                      className={`conclusion-select ${r.conclusion_choice}`}
                      value={r.conclusion_choice}
                      onChange={(e) => handleConclusionChange(r.task_id, 'conclusion_choice', e.target.value)}
                    >
                      {CONCLUSIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                  </td>
                  <td style={{ minWidth: 200 }}>
                    <input
                      type="text"
                      className="tasks-input"
                      style={{ fontSize: 13 }}
                      placeholder="Кратко…"
                      defaultValue={r.conclusion_text}
                      onBlur={(e) => {
                        if (e.target.value !== r.conclusion_text) {
                          handleConclusionChange(r.task_id, 'conclusion_text', e.target.value);
                        }
                      }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
